import { useEffect, useRef, useState } from "react";

import {
  BracesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DatabaseIcon,
  MessageSquareIcon,
  MousePointer2Icon,
  SquareDashedMousePointerIcon,
} from "lucide-react";

import type { ReactGrabAPI } from "react-grab";
import { Box, Stack } from "@mui/material";
import { IgDivider } from "@intelligems/ig-theme";

import { GrabbedContentDialog } from "./components/GrabbedContentDialog";
import { ReactQueryPanel } from "./components/ReactQueryPanel";
import { ToolbarButton } from "./components/ToolbarButton";
import { VariablesPanel } from "./components/VariablesPanel";
import { DESIGN_TOKENS, Z_INDEX_LEVELS } from "./constants";

const ICON_SIZE = 12;
const TOOLBAR_VISIBLE_KEY = "devtools:toolbar-visible";

type OpenPanel = "query" | "variables" | null;

export const Devtools = () => {
  const [api, setApi] = useState<ReactGrabAPI | null>(null);
  const [openPanel, setOpenPanel] = useState<OpenPanel>(null);
  const [grabbedContent, setGrabbedContent] = useState<string | null>(null);
  const [toolbarVisible, setToolbarVisible] = useState(
    () => localStorage.getItem(TOOLBAR_VISIBLE_KEY) !== "false"
  );
  const selectRef = useRef<HTMLButtonElement>(null);
  const inspectRef = useRef<HTMLButtonElement>(null);
  // True while selection mode was started from the Inspect button.
  const inspectModeRef = useRef(false);
  // True from an inspect-mode selection until its async copy settles —
  // selection deactivates react-grab before the clipboard write resolves.
  const inspectCopyPendingRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(TOOLBAR_VISIBLE_KEY, String(toolbarVisible));
  }, [toolbarVisible]);

  // Only one panel is ever open.
  const togglePanel = (panel: Exclude<OpenPanel, null>) =>
    setOpenPanel((cur) => (cur === panel ? null : panel));

  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | undefined;
    const setButtonActive = (button: HTMLButtonElement | null, on: boolean) => {
      if (button) {
        button.dataset.active = String(on);
      }
    };
    const markInspectCopyPending = () => {
      if (inspectModeRef.current) {
        inspectCopyPendingRef.current = true;
      }
    };

    void import("react-grab").then(
      ({ registerPlugin, unregisterPlugin, getGlobalApi }) => {
        if (cancelled) {
          return;
        }
        setApi(getGlobalApi());
        registerPlugin({
          name: "custom-toolbar",
          theme: { toolbar: { enabled: false } },
          hooks: {
            onActivate: () =>
              setButtonActive(
                inspectModeRef.current ? inspectRef.current : selectRef.current,
                true
              ),
            onDeactivate: () => {
              inspectModeRef.current = false;
              setButtonActive(selectRef.current, false);
              setButtonActive(inspectRef.current, false);
            },
            onElementSelect: markInspectCopyPending,
            onDragEnd: markInspectCopyPending,
            onCopySuccess: (_elements, content) => {
              if (!inspectCopyPendingRef.current) {
                return;
              }
              inspectCopyPendingRef.current = false;
              // react-grab may still be freezing React state updates here;
              // defer so the dialog state lands after it unfreezes.
              setTimeout(() => setGrabbedContent(content), 0);
            },
            onCopyError: () => {
              inspectCopyPendingRef.current = false;
            },
          },
        });
        cleanup = () => unregisterPlugin("custom-toolbar");
      }
    );

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);

  if (!api) {
    return null;
  }

  const handleInspectClick = () => {
    if (api.isActive()) {
      api.deactivate();
      return;
    }
    inspectModeRef.current = true;
    api.activate();
  };

  return (
    <>
      {!toolbarVisible && (
        <Box
          sx={{
            ...DESIGN_TOKENS.container,
            position: "fixed",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            zIndex: Z_INDEX_LEVELS.top,
          }}
        >
          <ToolbarButton
            onClick={() => setToolbarVisible(true)}
            title="Show toolbar"
          >
            <ChevronUpIcon size={ICON_SIZE} />
          </ToolbarButton>
        </Box>
      )}

      {toolbarVisible && (
        <Stack
          direction="row"
          sx={{
            ...DESIGN_TOKENS.container,
            alignItems: "center",
            position: "fixed",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: Z_INDEX_LEVELS.top,
          }}
        >
          <ToolbarButton
            onClick={() => api.toggle()}
            ref={selectRef}
            title="Select element"
          >
            <MousePointer2Icon size={ICON_SIZE} />
          </ToolbarButton>

          <ToolbarButton
            onClick={handleInspectClick}
            ref={inspectRef}
            title="Select element and preview copied context"
          >
            <SquareDashedMousePointerIcon size={ICON_SIZE} />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => api.comment()}
            title="Comment on element"
          >
            <MessageSquareIcon size={ICON_SIZE} />
          </ToolbarButton>

          <IgDivider orientation="vertical" sx={DESIGN_TOKENS.divider} />

          <ToolbarButton
            active={openPanel === "query"}
            onClick={() => togglePanel("query")}
            title="TanStack Query devtools"
          >
            <DatabaseIcon size={ICON_SIZE} />
          </ToolbarButton>

          <ToolbarButton
            active={openPanel === "variables"}
            onClick={() => togglePanel("variables")}
            title="Variables"
          >
            <BracesIcon size={ICON_SIZE} />
          </ToolbarButton>

          <IgDivider orientation="vertical" sx={DESIGN_TOKENS.divider} />

          <ToolbarButton
            onClick={() => setToolbarVisible(false)}
            title="Hide toolbar"
          >
            <ChevronDownIcon size={ICON_SIZE} />
          </ToolbarButton>
        </Stack>
      )}

      {openPanel === "query" && (
        <ReactQueryPanel onClose={() => setOpenPanel(null)} />
      )}
      {openPanel === "variables" && (
        <VariablesPanel onClose={() => setOpenPanel(null)} />
      )}

      {grabbedContent !== null && (
        <GrabbedContentDialog
          content={grabbedContent}
          onClose={() => setGrabbedContent(null)}
        />
      )}
    </>
  );
};
